<template>
  <div class="tl-page">
    <Header />

    <div class="tl-calendar-app">
      <!-- ── Header Bar ── -->
      <div class="tl-header-bar">
        <div class="tl-header-left">
          <h1 class="tl-page-title">
            <svg class="tl-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            River Calendar
          </h1>
        </div>

        <!-- ── Navigation Controls ── -->
        <div class="tl-header-center">
          <div class="tl-nav-controls">
            <button class="tl-nav-btn" @click="prev" title="Previous">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <h2 class="tl-current-range">{{ currentTitle }}</h2>
            <button class="tl-nav-btn" @click="next" title="Next">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <button class="tl-btn-today" @click="today">Today</button>
        </div>

        <div class="tl-header-actions">
          <!-- ── Ship Filter ── -->
          <div class="tl-filter-container" v-click-outside="closeShipFilter">
            <button class="tl-filter-trigger" @click="toggleShipFilter"
              :class="{ 'is-active': selectedShips.length > 0 }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>{{ filterLabel }}</span>
              <svg class="tl-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="3">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            <div v-if="showShipFilter" class="tl-filter-dropdown">
              <div class="tl-dropdown-header">
                <span class="tl-dropdown-title">Filter Ships</span>
                <button class="tl-btn-clear" @click="clearShips" v-if="selectedShips.length > 0">Clear All</button>
              </div>
              <div class="tl-dropdown-list">
                <label v-for="ship in allShipNames" :key="ship" class="tl-checkbox-item">
                  <input type="checkbox" :value="ship" v-model="selectedShips" @change="applyShipFilter">
                  <span class="tl-checkbox-custom"></span>
                  <span class="tl-checkbox-label">{{ ship }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="tl-btn-group">
            <button class="tl-btn-tab" :class="{ 'is-active': currentView === 'dayGridMonth' }"
              @click="changeView('dayGridMonth')">Month</button>
            <button class="tl-btn-tab" :class="{ 'is-active': currentView === 'dayGridWeek' }"
              @click="changeView('dayGridWeek')">Week</button>
          </div>
          <button class="tl-btn tl-btn--primary" @click="downloadPDF">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            PDF
          </button>
        </div>
      </div>

      <!-- ── Calendar Container ── -->
      <div class="tl-calendar-container" id="calendar-capture">
        <FullCalendar :options="calendarOptions" ref="calendar" />
      </div>
    </div>
  </div>
</template>

<script>
import Header from './Header.vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export default {
  name: 'Calender',
  components: {
    Header,
    FullCalendar
  },
  data() {
    return {
      currentView: 'dayGridMonth',
      currentTitle: '',
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        headerToolbar: false,
        dayMaxEvents: false, // Show all events, no "+more"
        eventDisplay: 'block',
        height: 'auto',
        contentHeight: 'auto',
        eventContent: this.renderEventContent,
        datesSet: this.handleDatesSet,
        events: []
      },
      allEvents: [],
      allShipNames: [],
      selectedShips: [],
      showShipFilter: false
    }
  },
  computed: {
    filterLabel() {
      if (this.selectedShips.length === 0) return 'All Ships';
      if (this.selectedShips.length === 1) return this.selectedShips[0];
      return `${this.selectedShips.length} Ships Selected`;
    }
  },
  directives: {
    clickOutside: {
      beforeMount(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
  created() {
    this.fetchEvents();
  },
  methods: {
    fetchEvents() {
      this.$xhr.get('calender.json').then(response => {
        const shipSchedules = response.data.slice(1);

        const shipColors = {
          'AmaPrima': '#ef4444',
          'AmaSiena': '#22c55e',
          'AmaSerena': '#f97316',
          'AmaCerto': '#ea580c',
          'AmaLucia': '#ec4899',
          'AmaMora': '#8b5cf6',
          'AmaKristina': '#b45309',
          'AmaStella': '#06b6d4',
          'AmaSofia': '#0891b2',
          'AmaVenita': '#3b82f6',
          'AmaViola': '#2563eb',
          'AmaReina': '#6366f1',
          '^Travelmarvel': '#14b8a6',
          '^Viking': '#0ea5e9',
          '^Viva': '#64748b',
          '^MS Swiss Ruby': '#475569',
          '^Avalon': '#4d7c0f',
          '^Emerald': '#10b981',
          '^Scenic': '#059669',
          '^nicko': '#eab308',
          '^MS Rhein Symphonie': '#ca8a04',
          '^MS Rhein Melodie': '#a16207',
          '^MS Belvedere': '#854d0e',
          '^Imperial City Tour': '#d946ef',
          '^SS Antoinette': '#f87171',
          '^River Queen': '#fb7185',
          '^SS Victoria': '#fda4af',
          '^APT': '#fbbf24',
          'River Crown': '#d97706',
          'Riverside Debussy': '#a21caf'
        };

        const groupByDate = {};
        shipSchedules.forEach(schedule => {
          if (!schedule.Date) return;
          const date = schedule.Date.substring(0, 10);
          const shipCode = `${schedule.Ship}_${schedule.Code}`;
          if (!groupByDate[date]) groupByDate[date] = {};
          if (!groupByDate[date][shipCode]) groupByDate[date][shipCode] = [];
          groupByDate[date][shipCode].push(schedule);
        });

        const events = [];
        Object.keys(groupByDate).forEach(date => {
          Object.keys(groupByDate[date]).forEach(shipCode => {
            const schedules = groupByDate[date][shipCode];
            const ship = schedules[0].Ship;
            const code = schedules[0].Code;

            const names = [...new Set(schedules.map(s => s.Name).filter(Boolean))];
            const nameSuffix = names.length > 0 ? ` (${names[0]})` : '';

            const title = `${ship} ${code}${nameSuffix}`;
            // Removed subTitle as we are showing all events individually per shipCode
            const description = schedules.map(s => s.Name).join(', ');

            let color = '#64748b';
            for (const shipRegex in shipColors) {
              const regex = new RegExp(shipRegex, 'i');
              if (regex.test(ship)) {
                color = shipColors[shipRegex];
                break;
              }
            }

            events.push({
              title,
              start: date,
              allDay: true,
              backgroundColor: color,
              borderColor: color,
              textColor: this.getContrastColor(color),
              extendedProps: {
                description,
                ship: ship
              }
            });
          });
        });

        this.allEvents = events;
        // Extract unique ship names and sort them
        this.allShipNames = [...new Set(events.map(e => e.extendedProps.ship))].sort();
        this.applyShipFilter();
      });
    },
    toggleShipFilter() {
      this.showShipFilter = !this.showShipFilter;
    },
    closeShipFilter() {
      this.showShipFilter = false;
    },
    clearShips() {
      this.selectedShips = [];
      this.applyShipFilter();
    },
    applyShipFilter() {
      if (this.selectedShips.length === 0) {
        this.calendarOptions.events = this.allEvents;
      } else {
        this.calendarOptions.events = this.allEvents.filter(event =>
          this.selectedShips.includes(event.extendedProps.ship)
        );
      }
    },
    renderEventContent(info) {
      return {
        html: `
          <div class="tl-event-content">
            <div class="tl-event-title" title="${info.event.title}">${info.event.title}</div>
          </div>
        `
      };
    },
    handleDatesSet(arg) {
      this.currentTitle = arg.view.title;
    },
    getContrastColor(hexcolor) {
      if (!hexcolor || hexcolor.startsWith('linear')) return '#ffffff';
      if (hexcolor.startsWith('#')) hexcolor = hexcolor.slice(1);
      if (hexcolor.length === 3) hexcolor = hexcolor.split('').map(c => c + c).join('');
      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? '#111827' : '#ffffff';
    },
    changeView(view) {
      this.currentView = view;
      this.$refs.calendar.getApi().changeView(view);
    },
    next() {
      this.$refs.calendar.getApi().next();
    },
    prev() {
      this.$refs.calendar.getApi().prev();
    },
    today() {
      this.$refs.calendar.getApi().today();
    },
    async downloadPDF(e) {
      e.preventDefault();
      const element = document.querySelector('.tl-calendar-app');
      if (!element) return;

      const originalStyle = element.style.cssText;

      // 1. Force a very wide viewport to ensure all 7 columns (Sun-Sat) fit perfectly
      // We use 2000px to give plenty of horizontal room
      element.style.width = '2000px';
      element.style.maxWidth = 'none';
      element.style.height = 'auto';
      element.style.overflow = 'visible';

      try {
        // 2. Trigger a resize event so FullCalendar re-calculates column widths for the new size
        window.dispatchEvent(new Event('resize'));
        await new Promise(resolve => setTimeout(resolve, 300)); // Longer wait for re-render

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 2000,
          windowHeight: element.scrollHeight + 100
        });

        const imgData = canvas.toDataURL('image/png', 1.0);

        // 3. Match PDF dimensions to the high-res canvas
        const pdfWidth = canvas.width;
        const pdfHeight = canvas.height;

        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'l' : 'p',
          unit: 'px',
          format: [pdfWidth, pdfHeight]
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        pdf.save(`River_Calendar_${this.currentTitle.replace(/\s+/g, '_')}.pdf`);
      } catch (error) {
        console.error('PDF Generation failed:', error);
      } finally {
        element.style.cssText = originalStyle;
        window.dispatchEvent(new Event('resize')); // Restore UI
      }
    }
  }
}
</script>

<style scoped>
/* ────────────── Page Layout ────────────── */
.tl-page {
  background: #f8fafc;
  min-height: 100vh;
}

.tl-calendar-app {
  padding: 24px 20px;
  max-width: 100%;
  /* Allow full width for large schedules */
  margin: 0 auto;
}

/* ────────────── Header Bar ────────────── */
.tl-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-header-left {
  flex: 1;
}

.tl-page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  text-transform: uppercase;
}

.tl-title-icon {
  width: 24px;
  height: 24px;
  color: #E2923D;
}

/* Header Center - Navigation */
.tl-header-center {
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tl-nav-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tl-nav-btn {
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.tl-nav-btn:hover {
  background: #e2e8f0;
  color: #E2923D;
}

.tl-current-range {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1e293b;
  min-width: 200px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tl-btn-today {
  font-size: 0.7rem;
  font-weight: 800;
  color: #E2923D;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  padding: 2px 10px;
  border-radius: 20px;
  cursor: pointer;
  text-transform: uppercase;
}

.tl-header-actions {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

/* ────────────── Tabs & Groups ────────────── */
.tl-btn-group {
  display: flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
}

.tl-btn-tab {
  border: none;
  background: transparent;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s;
}

.tl-btn-tab.is-active {
  background: #fff;
  color: #1e293b;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* ────────────── Buttons ────────────── */
.tl-btn {
  height: 34px;
  padding: 0 14px;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
}

.tl-btn--primary {
  background: #E2923D;
  color: white;
}

/* ────────────── Ship Filter ────────────── */
.tl-filter-container {
  position: relative;
}

.tl-filter-trigger {
  height: 34px;
  padding: 0 14px;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  min-width: 140px;
}

.tl-filter-trigger:hover {
  border-color: #cbd5e1;
  color: #1e293b;
}

.tl-filter-trigger.is-active {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #ea580c;
}

.tl-chevron {
  margin-left: auto;
  transition: transform 0.2s;
}

.tl-filter-trigger[aria-expanded="true"] .tl-chevron {
  transform: rotate(180deg);
}

.tl-filter-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 250px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 100;
  overflow: hidden;
  animation: tl-dropdown-in 0.2s ease-out;
}

@keyframes tl-dropdown-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tl-dropdown-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tl-dropdown-title {
  font-size: 0.75rem;
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tl-btn-clear {
  background: none;
  border: none;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ef4444;
  cursor: pointer;
  padding: 0;
}

.tl-btn-clear:hover {
  text-decoration: underline;
}

.tl-dropdown-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.tl-checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.tl-checkbox-item:hover {
  background: #f8fafc;
}

.tl-checkbox-item input {
  display: none;
}

.tl-checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #e2e8f0;
  border-radius: 5px;
  position: relative;
  transition: all 0.2s;
}

.tl-checkbox-item input:checked+.tl-checkbox-custom {
  background: #ea580c;
  border-color: #ea580c;
}

.tl-checkbox-item input:checked+.tl-checkbox-custom::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.tl-checkbox-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
}

.tl-dropdown-list::-webkit-scrollbar {
  width: 4px;
}

.tl-dropdown-list::-webkit-scrollbar-track {
  background: transparent;
}

.tl-dropdown-list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

/* ────────────── Calendar ────────────── */
.tl-calendar-container {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

::v-deep .fc {
  --fc-border-color: #f1f5f9;
  --fc-daygrid-event-dot-width: 6px;
  --fc-today-bg-color: #fefce8;
  font-family: inherit;
}

::v-deep .fc-toolbar {
  display: none !important;
}

::v-deep .fc-col-header-cell {
  background: #f8fafc;
  padding: 16px 0;
  border-bottom: 2px solid #e2e8f0;
}

::v-deep .fc-col-header-cell-cushion {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.1em;
  text-decoration: none !important;
}

::v-deep .fc-daygrid-day-frame {
  min-height: 150px !important;
  /* Increased height */
}

::v-deep .fc-daygrid-day-number {
  font-size: 0.95rem;
  font-weight: 700;
  color: #475569;
  padding: 10px 14px !important;
  text-decoration: none !important;
}

::v-deep .fc-day-today .fc-daygrid-day-number {
  color: #E2923D;
  font-weight: 900;
  background: #fff7ed;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 4px;
}

/* Events Styling - INCREASED SIZE */
::v-deep .fc-event {
  border-radius: 50px !important;
  /* Rounded pill style */
  margin: 2px 4px !important;
  border: none !important;
}

::v-deep .tl-event-content {
  padding: 4px 10px;
  line-height: 1.4;
}

::v-deep .tl-event-title {
  font-size: 0.78rem;
  /* Increased font size */
  font-weight: 800;
  white-space: normal;
  word-break: break-word;
}

/* Remove "more" link since we show everything */
::v-deep .fc-daygrid-more-link {
  display: none !important;
}

/* Ensure calendar cells expand for content */
::v-deep .fc-daygrid-day-events {
  margin-bottom: 4px;
}
</style>